/**
 * WizardStepVocals — Step 2: Capture current VocalForge configuration.
 * Reads from VocalConfigContext and shows a preview.
 */

import React, { useState } from 'react';
import { Button, Badge, TextArea } from '@components/ui';
import { Mic2, Check, SkipForward, PenLine } from 'lucide-react';
import { useVocalConfig } from '@context/VocalConfigContext';

interface WizardStepVocalsProps {
  vocalConfigJson: string | null;
  vocalArchitecture: string;
  onCapture: (configJson: string, architecture: string) => void;
  onSkip: () => void;
}

export const WizardStepVocals: React.FC<WizardStepVocalsProps> = ({
  vocalConfigJson,
  vocalArchitecture,
  onCapture,
  onSkip,
}) => {
  const { config, getActiveVocalArchitecture } = useVocalConfig();
  const [manualMode, setManualMode] = useState(false);
  const [manualText, setManualText] = useState(vocalArchitecture);
  const isCaptured = vocalArchitecture.length > 0;

  const handleCapture = () => {
    const arch = getActiveVocalArchitecture() || '';
    onCapture(JSON.stringify(config), arch);
  };

  const handleManualSave = () => {
    // No VocalForge config to attach — architecture text is hand-typed, config JSON stays null
    onCapture('', manualText);
    setManualMode(false);
  };

  // Preview what's currently configured
  const packName = config.mode === 'blend'
    ? `Blend: ${config.blendConfig?.packAKey || '?'} + ${config.blendConfig?.packBKey || '?'}`
    : config.singlePackKey || 'None selected';

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-400">
        Capture your current VocalForge configuration, or type/paste a vocal architecture directly.
      </p>

      {!manualMode && (
        <>
          {/* Current config preview */}
          <div className="p-4 bg-slate-900/50 rounded-xl border border-slate-800">
            <div className="flex items-center gap-2 mb-3">
              <Mic2 size={16} className="text-slate-500" />
              <span className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Current VocalForge Config</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <Badge variant="primary">{config.mode === 'blend' ? 'Blend Mode' : 'Single Pack'}</Badge>
              <Badge variant="info">{packName}</Badge>
              {config.includeHarmonyStack && <Badge variant="default-muted">Harmony Stack</Badge>}
              {config.includeChoirGang && <Badge variant="default-muted">Choir/Gang</Badge>}
              {config.scream?.typeId && <Badge variant="warning">Scream Aug</Badge>}
              {config.deepDive?.dialectId && <Badge variant="default-muted">Dialect</Badge>}
              {config.deepDive?.agingId && <Badge variant="default-muted">Aging</Badge>}
              {config.deepDive?.breathPatternId && <Badge variant="default-muted">Breath</Badge>}
              {config.deepDive?.emotionalArcId && <Badge variant="default-muted">Arc</Badge>}
            </div>
          </div>

          {/* Capture status */}
          {isCaptured ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 bg-green-950/20 border border-green-900/30 rounded-xl">
                <Check size={16} className="text-green-400" />
                <span className="text-sm text-green-300 font-bold">
                  {vocalConfigJson ? 'Vocal config captured' : 'Manual vocal architecture entered'}
                </span>
                <span className="text-[10px] text-slate-500 ml-auto">{vocalArchitecture.length} chars</span>
              </div>
              <Button variant="ghost" size="sm" leftIcon={<Mic2 size={14} />} onClick={handleCapture}>
                Re-capture from Current Config
              </Button>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button
                variant="primary"
                size="sm"
                fullWidth
                leftIcon={<Mic2 size={14} />}
                onClick={handleCapture}
              >
                Capture Current VocalForge Config
              </Button>
              <Button
                variant="ghost"
                size="sm"
                leftIcon={<SkipForward size={14} />}
                onClick={onSkip}
              >
                Skip
              </Button>
            </div>
          )}

          <button
            onClick={() => { setManualText(vocalArchitecture); setManualMode(true); }}
            className="text-xs text-slate-500 hover:text-amber-400 transition-colors flex items-center gap-1.5"
          >
            <PenLine size={12} /> {isCaptured && !vocalConfigJson ? 'Edit manual entry' : 'Type it in manually instead'}
          </button>
        </>
      )}

      {manualMode && (
        <div className="space-y-3">
          <TextArea
            value={manualText}
            onChange={e => setManualText(e.target.value)}
            placeholder="Describe the vocal architecture directly — timbre, delivery, accent, effects chain, harmony/gang vocal notes, etc."
            rows={10}
            className="!bg-slate-900 !border-slate-700 font-mono text-xs"
          />
          <div className="flex gap-3">
            <Button variant="primary" size="sm" leftIcon={<Check size={14} />} onClick={handleManualSave} disabled={!manualText.trim()}>
              Save Manual Entry
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setManualMode(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
